import React from "react";
import { ArrowRight, MapPin, Clock, Ticket, Car, Info } from "lucide-react";
import LocationMap from "@/components/content/LocationMap";
import PlaceholderNote from "@/components/content/PlaceholderNote";
import Seo from "@/components/Seo";
import { LLink } from "@/i18n/LLink";
import { useLang } from "@/i18n/LangContext";
import { pageContent, pickLang } from "@/i18n/pageContent";

const WOUDAGEMAAL = { lat: 52.8398, lng: 5.7237 };

const ContentEyebrow = ({ children }) => (
  <span className="inline-block text-[#2E9CD6] font-[Poppins] font-semibold text-xs uppercase tracking-[0.2em] mb-3">
    {children}
  </span>
);

const AccentLine = () => (
  <div className="flex items-center gap-3 mb-6" aria-hidden="true">
    <span className="block w-10 h-[3px] rounded-full bg-[#2E9CD6]" />
    <svg width="60" height="10" viewBox="0 0 60 10" className="text-[#6BC0E8]">
      <path
        d="M0,5 C10,0 20,10 30,5 C40,0 50,10 60,5"
        stroke="currentColor"
        strokeWidth="2"
        fill="none"
        strokeLinecap="round"
      />
    </svg>
  </div>
);

export const WoudagemaalPage = () => {
  const { lang } = useLang();
  const c = pickLang(pageContent.woudagemaal, lang);
  const home = pickLang(pageContent.home, lang);
  const ctaEten = home.categories.items["eten-drinken"].title;
  const ctaWatersport = home.categories.items["watersport-haven"].title;
  const ctaZienDoen = home.categories.items["zien-doen"].title;

  return (
    <div data-testid="woudagemaal-page" className="bg-white">
      <Seo title={c.seo.title} description={c.seo.description} lang={lang} />

      {/* Hero */}
      <section
        data-testid="woudagemaal-hero"
        className="relative w-full overflow-hidden"
        style={{ minHeight: "60vh" }}
      >
        <div className="absolute inset-0">
          <img
            src="/media/woudagemaal.jpg"
            alt={c.h1}
            className="w-full h-full object-cover"
            loading="eager"
          />
          <div
            className="absolute inset-0"
            style={{
              background:
                "linear-gradient(180deg, rgba(23,58,94,0.35) 0%, rgba(23,58,94,0.35) 40%, rgba(23,58,94,0.85) 100%)",
            }}
          />
        </div>
        <div className="ll-container relative z-10 py-24 sm:py-32">
          <div className="max-w-3xl">
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[#173A5E] bg-[#F3C300] font-[Poppins] font-bold text-xs uppercase tracking-wider mb-4 ll-fade-up">
              {c.heroBadge}
            </span>
            <h1
              className="text-white text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-tight mb-4 ll-fade-up"
              style={{ animationDelay: "0.05s" }}
            >
              {c.h1}
            </h1>
          </div>
        </div>
      </section>

      {/* Intro */}
      <section className="py-14 sm:py-20">
        <div className="ll-container max-w-3xl">
          {c.intro.map((p, i) => (
            <p
              key={i}
              className={`text-[#3E5871] font-[Inter] text-lg sm:text-xl leading-relaxed ${
                i === 0 ? "mb-4" : ""
              }`}
            >
              {p}
            </p>
          ))}
        </div>
      </section>

      {/* Waarom het zo bijzonder is */}
      <section className="ll-section-soft py-14 sm:py-20">
        <div className="ll-container max-w-3xl">
          <ContentEyebrow>{c.waarom.eyebrow}</ContentEyebrow>
          <h2 className="text-3xl sm:text-4xl font-bold mb-3">
            {c.waarom.title}
          </h2>
          <AccentLine />
          {c.waarom.body.map((p, i) => (
            <p
              key={i}
              className={`text-[#3E5871] font-[Inter] text-base sm:text-lg leading-relaxed ${
                i === 0 ? "mb-4" : ""
              }`}
            >
              {p}
            </p>
          ))}
        </div>
      </section>

      {/* Wat je ziet en beleeft */}
      <section className="py-14 sm:py-20">
        <div className="ll-container">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
            <div>
              <ContentEyebrow>{c.watJeZiet.eyebrow}</ContentEyebrow>
              <h2 className="text-3xl sm:text-4xl font-bold mb-3">
                {c.watJeZiet.title}
              </h2>
              <AccentLine />
              {c.watJeZiet.body.map((p, i) => (
                <p
                  key={i}
                  className="text-[#3E5871] font-[Inter] text-base sm:text-lg leading-relaxed mb-4"
                >
                  {p}
                </p>
              ))}
              <p className="text-sm text-[#5A7594] font-[Inter]">
                <PlaceholderNote>{c.watJeZiet.note}</PlaceholderNote>
              </p>
            </div>
            <div>
              <img
                src="/media/woudagemaal-wijd.jpg"
                alt={c.h1}
                className="w-full h-auto rounded-2xl shadow-md object-cover aspect-[4/3]"
                loading="lazy"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Goed om te weten */}
      <section className="ll-section-soft py-14 sm:py-20">
        <div className="ll-container">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
            <div>
              <ContentEyebrow>{c.goedOmTeWeten.eyebrow}</ContentEyebrow>
              <h2 className="text-3xl sm:text-4xl font-bold mb-3">
                {c.goedOmTeWeten.title}
              </h2>
              <AccentLine />
              <dl className="space-y-4">
                <div className="flex items-start gap-3">
                  <MapPin className="w-5 h-5 text-[#2E9CD6] mt-1 shrink-0" />
                  <div>
                    <dt className="font-[Poppins] font-semibold text-[#173A5E]">
                      {c.goedOmTeWeten.waar.label}
                    </dt>
                    <dd className="text-[#3E5871] font-[Inter]">
                      {c.goedOmTeWeten.waar.value}
                      <PlaceholderNote>
                        {c.goedOmTeWeten.waar.placeholder}
                      </PlaceholderNote>
                    </dd>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Clock className="w-5 h-5 text-[#2E9CD6] mt-1 shrink-0" />
                  <div>
                    <dt className="font-[Poppins] font-semibold text-[#173A5E]">
                      {c.goedOmTeWeten.openingstijden.label}
                    </dt>
                    <dd className="text-[#3E5871] font-[Inter]">
                      <PlaceholderNote>
                        {c.goedOmTeWeten.openingstijden.placeholder}
                      </PlaceholderNote>
                    </dd>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Ticket className="w-5 h-5 text-[#2E9CD6] mt-1 shrink-0" />
                  <div>
                    <dt className="font-[Poppins] font-semibold text-[#173A5E]">
                      {c.goedOmTeWeten.entree.label}
                    </dt>
                    <dd className="text-[#3E5871] font-[Inter]">
                      <PlaceholderNote>
                        {c.goedOmTeWeten.entree.placeholder}
                      </PlaceholderNote>
                    </dd>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Info className="w-5 h-5 text-[#2E9CD6] mt-1 shrink-0" />
                  <div>
                    <dt className="font-[Poppins] font-semibold text-[#173A5E]">
                      {c.goedOmTeWeten.bezoekerscentrum.label}
                    </dt>
                    <dd className="text-[#3E5871] font-[Inter]">
                      {c.goedOmTeWeten.bezoekerscentrum.value}
                    </dd>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Car className="w-5 h-5 text-[#2E9CD6] mt-1 shrink-0" />
                  <div>
                    <dt className="font-[Poppins] font-semibold text-[#173A5E]">
                      {c.goedOmTeWeten.hoeErKomen.label}
                    </dt>
                    <dd className="text-[#3E5871] font-[Inter]">
                      {c.goedOmTeWeten.hoeErKomen.value}
                      <PlaceholderNote>
                        {c.goedOmTeWeten.hoeErKomen.placeholder}
                      </PlaceholderNote>
                    </dd>
                  </div>
                </div>
              </dl>
            </div>
            <div>
              <LocationMap
                lat={WOUDAGEMAAL.lat}
                lng={WOUDAGEMAAL.lng}
                name="Ir. D.F. Woudagemaal"
                address="Tacozijl, Lemmer"
                zoom={14}
                height={420}
              />
            </div>
          </div>
        </div>
      </section>

      {/* Combineer */}
      <section className="py-14 sm:py-20">
        <div className="ll-container max-w-3xl">
          <ContentEyebrow>{c.combineer.eyebrow}</ContentEyebrow>
          <h2 className="text-3xl sm:text-4xl font-bold mb-3">
            {c.combineer.title}
          </h2>
          <AccentLine />
          <p className="text-[#3E5871] font-[Inter] text-base sm:text-lg leading-relaxed mb-8">
            {c.combineer.body}
          </p>
          <div className="flex flex-wrap gap-3">
            <LLink to="/eten-en-drinken" className="ll-btn-primary">
              {ctaEten}
              <ArrowRight className="w-4 h-4" />
            </LLink>
            <LLink to="/watersport-haven" className="ll-btn-accent">
              {ctaWatersport}
              <ArrowRight className="w-4 h-4" />
            </LLink>
            <LLink to="/zien-en-doen" className="ll-btn-outline">
              {ctaZienDoen}
              <ArrowRight className="w-4 h-4" />
            </LLink>
          </div>
        </div>
      </section>
    </div>
  );
};

export default WoudagemaalPage;
